import React, { useMemo } from "react";
import HealthCard from "./HealthCard";
import {
  BAR_COLORS,
  donutDash,
  formatNumber,
  averageMapValues,
  maintenanceCostEfficiencyScore,
} from "../utils";

/**
 * Weights (image):
 * Vehicle Availability              31.25%
 * Preventive Maintenance Compliance 25%
 * Breakdown Rate                    25%
 * Maintenance Cost Efficiency       18.75%
 *
 * Contribution = value * (weight / 100)
 * e.g. weight 20%, value 20 → 4
 * Total score = sum of contributions (out of 100)
 *
 * Sources: fmd/20, fmd/21, fmd/27, fmd/14 (+ fmd/26 via costPerKm.previous when available)
 */
const WEIGHTS = {
  availability: 31.25,
  pmCompliance: 25,
  breakdownRate: 25,
  costEfficiency: 18.75,
};

const CATEGORY_COLORS = {
  availability: BAR_COLORS[0], // blue
  pmCompliance: BAR_COLORS[1], // green
  breakdownRate: BAR_COLORS[2], // amber
  costEfficiency: BAR_COLORS[3], // red
};

const clamp = (value, min = 0, max = 100) =>
  Math.max(min, Math.min(max, Number(value) || 0));

/** fmd/20 — use availabilityPercent directly. */
const getAvailabilityValue = (data) =>
  clamp(Number(data?.availabilityPercent ?? 0));

const getPmComplianceValue = (data) => clamp(data?.pmCompliancePercent ?? 0);

/**
 * fmd/27 — use 30-60-days.unexpectedPercentage.
 * Lower unexpected % is better → invert to 0–100 health score.
 */
const getBreakdownValue = (data) => {
  const period = data?.["30-60-days"] || data?.days30to60 || {};
  const unexpected = Number(period.unexpectedPercentage ?? 0);
  return clamp(100 - unexpected);
};

/**
 * fmd/14 current vs fmd/26 previous — average costPerKm, map delta to score bands.
 */
const getCostEfficiencyValue = (costPerKm) => {
  const currentMap =
    costPerKm?.current?.costPerKm || costPerKm?.costPerKm || {};
  const previousMap =
    costPerKm?.previous?.costPerKm || costPerKm?.old?.costPerKm || {};
  const currentAvg = averageMapValues(currentMap);
  const previousAvg = averageMapValues(previousMap);
  return maintenanceCostEfficiencyScore(currentAvg, previousAvg);
};

const FleetHealthScore = ({ translate, data, loading }) => {
  const { score, categories, badge } = useMemo(() => {
    const availability = getAvailabilityValue(data?.availability);
    const pmCompliance = getPmComplianceValue(data?.pmCompliance);
    const breakdownRate = getBreakdownValue(data?.breakdowns);
    const costEfficiency = getCostEfficiencyValue(data?.costPerKm);

    const rows = [
      {
        key: "availability",
        label: translate("vehicleAvailability") || "Vehicle availability",
        weight: WEIGHTS.availability,
        value: availability,
        color: CATEGORY_COLORS.availability,
      },
      {
        key: "pmCompliance",
        label:
          translate("preventiveMaintenanceCompliance") ||
          translate("pmCompliance") ||
          "PM compliance",
        weight: WEIGHTS.pmCompliance,
        value: pmCompliance,
        color: CATEGORY_COLORS.pmCompliance,
      },
      {
        key: "breakdownRate",
        label: translate("breakdownRate") || "Breakdown rate",
        weight: WEIGHTS.breakdownRate,
        value: breakdownRate,
        color: CATEGORY_COLORS.breakdownRate,
      },
      {
        key: "costEfficiency",
        label:
          translate("maintenanceCostEfficiency") ||
          "Maintenance cost efficiency",
        weight: WEIGHTS.costEfficiency,
        value: costEfficiency,
        color: CATEGORY_COLORS.costEfficiency,
      },
    ].map((row) => ({
      ...row,
      // value * (weight / 100) — e.g. 20 * 0.20 = 4
      contribution: (row.value * row.weight) / 100,
    }));

    const total = rows.reduce((sum, row) => sum + row.contribution, 0);
    const rounded = Math.round(total);

    const badgeMeta =
      rounded >= 80
        ? {
            text: translate("good"),
            style: { background: "#e8f5e8", color: "#3f9c40" },
          }
        : rounded >= 60
          ? {
              text: translate("fair"),
              style: { background: "#fdf3e0", color: "#b3781a" },
            }
          : {
              text: translate("poor"),
              style: { background: "#fdeaea", color: "#e0484c" },
            };

    return {
      score: rounded,
      categories: rows,
      badge: badgeMeta,
    };
  }, [data, translate]);

  const { dashArray } = donutDash(score, 42);

  return (
    <HealthCard
      className="fh-span-5"
      title={translate("fleetHealthScore")}
      subtitle={translate("fleetHealthScoreSub")}
      badge={badge.text}
      badgeStyle={badge.style}
      loading={loading}
    >
      <div className="fh-score-layout">
        <svg
          width="110"
          height="110"
          viewBox="0 0 110 110"
          style={{ flex: "none" }}
        >
          <circle
            cx="55"
            cy="55"
            r="42"
            fill="none"
            stroke="#eef1f4"
            strokeWidth="12"
          />
          <circle
            cx="55"
            cy="55"
            r="42"
            fill="none"
            stroke="#8fd18f"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={dashArray}
            transform="rotate(-90 55 55)"
          />
          <text
            x="55"
            y="55"
            textAnchor="middle"
            fontSize="24"
            fontWeight="700"
            fill="#22303c"
          >
            {score}
          </text>
          <text x="55" y="70" textAnchor="middle" fontSize="10" fill="#8a97a3">
            {translate("outOf100")}
          </text>
        </svg>

        <div className="fh-score-bars">
          {categories.map((item) => (
            <div key={item.key}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  marginBottom: 2,
                  color: "#5c6a76",
                  gap: 8,
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 7,
                      height: 7,
                      borderRadius: 2,
                      background: item.color,
                      marginRight: 5,
                      verticalAlign: "middle",
                    }}
                  />
                  {item.label}
                  {/*  <i
                    style={{
                      color: "#8a97a3",
                      fontSize: 10,
                      marginLeft: 4,
                      fontStyle: "normal",
                    }}
                  >
                    {item.weight}%
                  </i>
                  */}
                </span>
                <b style={{ color: "#22303c", whiteSpace: "nowrap" }}>
                  {/* {formatNumber(item.contribution, 1)}*/}
                  <span
                    style={{
                      color: "#8a97a3",
                      fontWeight: 500,
                      marginLeft: 4,
                      fontSize: 10,
                    }}
                  >
                    ({formatNumber(item.value, 0)})
                  </span>
                </b>
              </div>
              <div className="fh-track" style={{ height: 5 }}>
                <div
                  className="fh-fill"
                  style={{
                    width: `${clamp((item.contribution / item.weight) * 100)}%`,
                    background: item.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </HealthCard>
  );
};

export default FleetHealthScore;
